/**
 * Redact obviously-sensitive values out of diagnostic text before it leaves the
 * browser.
 *
 * The client half of the policy `shared/utils/sensitive_text.py` implements on
 * the server, kept deliberately in step with it — same shapes, same order, same
 * `[redacted]` marker. Both halves exist for different reasons and neither is
 * redundant:
 *
 * - The CLIENT pass is what keeps a secret from ever crossing the wire, so it
 *   never sits in a request body, an access log or an edge trace. That is what
 *   "does not reach the server" means, and only this half can deliver it.
 * - The SERVER pass is what holds when a caller does not run this one.
 *
 * The policy — what is dropped, what is deliberately KEPT, and why — is written
 * down once in `docs/infrastructure/sysadmin-alerts-design.md`
 * § "Client-payload redaction".
 */

export const REDACTED = '[redacted]';

/**
 * What counts as a key whose VALUE is sensitive.
 *
 * Two arms, because one rule cannot serve both halves of the problem:
 *
 * - **Contains-match** — the word may sit inside a compound key. This arm is
 *   load-bearing, not a nicety: `accessToken`, `refreshToken`, `idToken`,
 *   `authToken` and `X-Api-Key` are the DEFAULT field names of every OAuth and
 *   JWT client library, so they are exactly what a `console.log(authResponse)`
 *   or a stringified fetch failure carries. A word-boundary-only rule misses all
 *   of them (`\btoken\b` finds no boundary inside `accessToken`), which is the
 *   commonest real secret this module exists to catch.
 * - **Whole-word** — for words short or ordinary enough that a contains-match
 *   would eat innocent text: `auth` inside `author`, `sid` inside `residual`,
 *   `pass` inside `passed`. Those keep their boundaries, and the named
 *   session-cookie keys are listed out rather than reached by a substring.
 */
const SENSITIVE_KEY =
  '(?:' +
  '[A-Za-z0-9_.\\-]*' +
  '(?:token|secret|passw(?:or)?d|api[_-]?key|apikey|credential|private[_-]?key)' +
  '[A-Za-z0-9_.\\-]*' +
  '|' +
  'auth|authorization|pass|otp|mfa[_-]?code|cvv|ssn' +
  '|sid|session|sessionid|jsessionid|phpsessid|connect\\.sid' +
  ')';

/**
 * Ordered: the most specific shapes first, so a JWT is not first mauled by the
 * generic key=value rule into something the JWT rule no longer recognises.
 */
const PATTERNS: ReadonlyArray<readonly [RegExp, string]> = [
  // `Authorization: <scheme> <credential>` for ANY scheme, not just Bearer. The
  // scheme is KEPT (an operator diagnoses from "this was Basic, not Bearer");
  // the credential goes. Written before the generic key=value rule because that
  // rule's value stops at the first space, which on
  // `Authorization: Basic YWxpY2U6cGFzc3dvcmQ=` redacts the word "Basic" and
  // leaves the base64 credential next to a marker claiming it was handled.
  [
    /\b(authorization\s*[:=]\s*)((?:bearer|basic|digest|token|apikey)\s+)?[A-Za-z0-9._~+/=-]{6,}/gi,
    `$1$2${REDACTED}`,
  ],
  // `Bearer eyJ...` standing on its own, with no `Authorization:` in front.
  [/\bBearer\s+[A-Za-z0-9._~+/=-]{8,}/gi, `Bearer ${REDACTED}`],
  // A JSON Web Token anywhere (three base64url segments).
  [/\beyJ[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}/g, REDACTED],
  // PanLuma API keys — `plma_live_{secret}` / `plma_test_{secret}`.
  [/\bplma_[a-z]+_[A-Za-z0-9_-]{8,}/g, REDACTED],
  // `key=value`, `key: value`, `"key": "value"` for any sensitive key name. The
  // value runs to the first delimiter so surrounding prose survives.
  [
    new RegExp(
      `(["']?\\b${SENSITIVE_KEY}\\b["']?\\s*[:=]\\s*)(?:"[^"]*"|'[^']*'|[^\\s,;&}\\]]+)`,
      'gi'
    ),
    `$1${REDACTED}`,
  ],
  // Email addresses.
  [/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, REDACTED],
  // A 13-to-19 digit run, optionally separated — a payment card. Before the bare
  // digit rule so a spaced card number is caught as one value.
  [/\b(?:\d[ -]?){12,18}\d\b/g, REDACTED],
  // Any remaining long bare digit run. The floor is 12 so a stack frame's
  // `:1234:56` line/column pair and an ordinary quantity survive untouched — an
  // operator diagnoses from those.
  [/\b\d{12,}\b/g, REDACTED],
];

/** Apply every pattern in the policy. `null`/`undefined` in, `null` out. */
export function redactClientText(text: string | null | undefined): string | null {
  if (text === null || text === undefined) return null;
  let out = text;
  for (const [pattern, replacement] of PATTERNS) {
    out = out.replace(pattern, replacement);
  }
  return out;
}
